import React, { useState, useEffect } from 'react';
import { getBoostAnalytics, processExpiredBoosts } from '../../services/api';
import { 
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiUsers,
  FiPackage,
  FiCalendar,
  FiRefreshCw,
  FiPieChart,
  FiBarChart2,
  FiActivity,
  FiDatabase,
  FiPercent,
  FiZap,
  FiStar,
  FiShoppingBag,
  FiGrid,
  FiCornerDownRight,
  FiTarget
} from 'react-icons/fi';

function BoostAnalytics({ admin }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [period, setPeriod] = useState('month');
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period, dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { period };
      if (dateRange.date_from) params.date_from = dateRange.date_from;
      if (dateRange.date_to) params.date_to = dateRange.date_to;

      const response = await getBoostAnalytics(params);
      if (response.success) {
        setAnalytics(response.data);
        setSuccessMessage('Analytics updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.message || 'Failed to load analytics');
      }
    } catch (err) {
      setError('Failed to load analytics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessExpired = async () => {
    if (!window.confirm('Process all expired boosts? This will mark expired boosts as inactive.')) {
      return;
    }

    try {
      setProcessing(true);
      const response = await processExpiredBoosts();
      if (response.success) {
        setSuccessMessage(`Successfully processed ${response.data.processed_count || 0} expired boosts!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchAnalytics();
      } else {
        throw new Error(response.message || 'Processing failed');
      }
    } catch (err) {
      setError('Failed to process expired boosts: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPeriodLabel = () => {
    const labels = {
      'today': 'Today',
      'week': 'Last 7 Days',
      'month': 'Last 30 Days',
      'year': 'Last 365 Days',
      'all': 'All Time'
    };
    return labels[period] || period;
  };

  // Loading Skeleton
  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            ))}
          </div>

          {/* Charts Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-96 bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="h-6 bg-gray-700 rounded w-1/3 mb-6"></div>
                <div className="h-64 bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!analytics && !loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-8xl mb-6">📊</div>
          <h3 className="text-2xl font-bold text-white mb-2">Analytics Unavailable</h3>
          <p className="text-gray-400 mb-6">Failed to load boost analytics data.</p>
          <button
            onClick={fetchAnalytics}
            className="px-6 py-3 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center mx-auto hover:scale-[1.02]"
          >
            <FiRefreshCw className="mr-2" />
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  const { revenue_overview, package_performance, user_engagement, active_boosts } = analytics;

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Boost</span> Analytics
            </h1>
            <p className="text-gray-400 mt-1">Track performance and revenue from listing boosts</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            {/* Date Range Selector */}
            <div className="flex gap-2">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="year">Last 365 Days</option>
                <option value="all">All Time</option>
              </select>
              <button
                onClick={fetchAnalytics}
                className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
              >
                <FiRefreshCw className="mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertTriangle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Period Info */}
      <div className="mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 flex items-center justify-between hover:border-gray-600 transition-colors">
          <div className="flex items-center space-x-3">
            <FiCalendar className="text-[#ff660e]" />
            <div>
              <h3 className="font-medium text-white">Reporting Period</h3>
              <p className="text-sm text-gray-400">{getPeriodLabel()}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleProcessExpired}
              disabled={processing || active_boosts.expired_boosts === 0}
              className="px-4 py-2 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 hover:text-blue-300 rounded-lg font-medium flex items-center transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed border border-blue-800/50"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FiRefreshCw className="mr-2" />
                  Process Expired ({active_boosts.expired_boosts || 0})
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Total Revenue */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Revenue</p>
              <p className="text-2xl font-bold text-[#ff660e] mt-1 group-hover:text-orange-400 transition-colors">
                {formatCurrency(revenue_overview.total_revenue)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {revenue_overview.successful_orders} successful orders
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[#ff660e]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiDollarSign className="text-[#ff660e] text-2xl" />
            </div>
          </div>
        </div>

        {/* Today's Revenue */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Today's Revenue</p>
              <p className="text-2xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">
                {formatCurrency(revenue_overview.revenue_today)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Revenue from today</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiTrendingUp className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* 7-Day Revenue */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">7-Day Revenue</p>
              <p className="text-2xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">
                {formatCurrency(revenue_overview.revenue_7d)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Last 7 days</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiBarChart2 className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>

        {/* Avg Order Value */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Avg Order Value</p>
              <p className="text-2xl font-bold text-purple-400 mt-1 group-hover:text-purple-300 transition-colors">
                {formatCurrency(revenue_overview.avg_order_value)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Per successful order</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiTarget className="text-purple-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Conversion & Orders Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Orders */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Total Orders</div>
            <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
              <FiShoppingBag className="text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white">{revenue_overview.total_orders}</div>
          <div className="flex items-center space-x-4 mt-4">
            <div className="flex-1">
              <div className="text-sm text-gray-400">Successful</div>
              <div className="text-lg font-semibold text-green-400">{revenue_overview.successful_orders}</div>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-400">Conversion</div>
              <div className="text-lg font-semibold text-[#ff660e]">
                {revenue_overview.total_orders > 0 
                  ? Math.round((revenue_overview.successful_orders / revenue_overview.total_orders) * 100) 
                  : 0}%
              </div>
            </div>
          </div>
        </div>

        {/* 30-Day Revenue */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">30-Day Revenue</div>
            <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center">
              <FiTrendingUp className="text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white">{formatCurrency(revenue_overview.revenue_30d)}</div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-400">7-Day</span>
              <span className="text-gray-300">{formatCurrency(revenue_overview.revenue_7d)}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-500"
                style={{
                  width: `${revenue_overview.revenue_7d > 0 && revenue_overview.revenue_30d > 0 
                    ? Math.min(100, (revenue_overview.revenue_7d / revenue_overview.revenue_30d) * 100)
                    : 0}%`
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Active Boosts Summary */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Active Boosts</div>
            <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
              <FiZap className="text-[#ff660e]" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white">{active_boosts.total_active_boosts}</div>
          <div className="flex items-center space-x-4 mt-4">
            <div className="flex-1">
              <div className="text-sm text-gray-400">Currently Active</div>
              <div className="text-lg font-semibold text-green-400">{active_boosts.currently_active}</div>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-400">Avg Duration</div>
              <div className="text-lg font-semibold text-blue-400">
                {Math.round(active_boosts.avg_boost_duration)} days
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Package Performance */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FiPackage className="mr-2 text-[#ff660e]" />
              Package Performance
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {package_performance.map((pkg) => {
                const conversionRate = parseFloat(pkg.conversion_rate) || 0;
                const totalOrders = parseInt(pkg.total_orders) || 0;
                const successfulOrders = parseInt(pkg.successful_orders) || 0;
                
                return (
                  <div key={pkg.package_id} className="flex items-center justify-between p-4 bg-gray-750 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-white">{pkg.name}</h4>
                        <span className="px-2 py-0.5 bg-blue-900/30 text-blue-400 text-xs font-medium rounded border border-blue-800/50">
                          {pkg.duration_days} days
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">
                        Price: <span className="font-medium text-gray-300">{formatCurrency(pkg.price)}</span>
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <p className="font-semibold text-white">{formatCurrency(pkg.total_revenue)}</p>
                      <div className="flex items-center justify-end space-x-2">
                        <span className="text-xs text-gray-400">
                          {successfulOrders}/{totalOrders} orders
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          conversionRate > 50 ? 'bg-green-900/30 text-green-400' : 
                          conversionRate > 20 ? 'bg-yellow-900/30 text-yellow-400' : 
                          'bg-red-900/30 text-red-400'
                        }`}>
                          {conversionRate > 0 ? conversionRate.toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* User Engagement & Active Boosts */}
        <div className="space-y-6">
          {/* User Engagement */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <FiUsers className="mr-2 text-blue-400" />
                User Engagement
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="w-16 h-16 rounded-full bg-blue-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiUsers className="text-blue-400 text-2xl" />
                  </div>
                  <p className="text-2xl font-bold text-white">{user_engagement.unique_customers || 0}</p>
                  <p className="text-sm text-gray-400 mt-1">Unique Customers</p>
                </div>
                <div>
                  <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiDollarSign className="text-green-400 text-2xl" />
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency(user_engagement.avg_spend_per_customer)}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">Avg Spend</p>
                </div>
                <div>
                  <div className="w-16 h-16 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiStar className="text-purple-400 text-2xl" />
                  </div>
                  <p className="text-2xl font-bold text-white">{user_engagement.repeat_customers || 0}</p>
                  <p className="text-sm text-gray-400 mt-1">Repeat Customers</p>
                </div>
              </div>
              
              {user_engagement.max_spend && (
                <div className="mt-6 p-4 bg-gray-750 rounded-xl border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Maximum Spend</p>
                      <p className="text-lg font-bold text-[#ff660e]">
                        {formatCurrency(user_engagement.max_spend)}
                      </p>
                    </div>
                    <FiTarget className="text-[#ff660e] text-xl" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Boosts Details */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <FiActivity className="mr-2 text-[#ff660e]" />
                Boost Performance
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6 text-center">
                <div className="p-4 bg-gray-750 rounded-xl border border-gray-700">
                  <div className="w-12 h-12 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiZap className="text-green-400 text-xl" />
                  </div>
                  <p className="text-2xl font-bold text-green-400">{active_boosts.currently_active || 0}</p>
                  <p className="text-sm text-gray-400 mt-1">Active Now</p>
                </div>
                <div className="p-4 bg-gray-750 rounded-xl border border-gray-700">
                  <div className="w-12 h-12 rounded-full bg-blue-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiDatabase className="text-blue-400 text-xl" />
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{active_boosts.total_active_boosts || 0}</p>
                  <p className="text-sm text-gray-400 mt-1">Total Active</p>
                </div>
                <div className="p-4 bg-gray-750 rounded-xl border border-gray-700">
                  <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiClock className="text-red-400 text-xl" />
                  </div>
                  <p className="text-2xl font-bold text-red-400">{active_boosts.expired_boosts || 0}</p>
                  <p className="text-sm text-gray-400 mt-1">Expired</p>
                </div>
                <div className="p-4 bg-gray-750 rounded-xl border border-gray-700">
                  <div className="w-12 h-12 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-3">
                    <FiCalendar className="text-purple-400 text-xl" />
                  </div>
                  <p className="text-2xl font-bold text-purple-400">
                    {Math.round(active_boosts.avg_boost_duration) || 0} days
                  </p>
                  <p className="text-sm text-gray-400 mt-1">Avg Duration</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BoostAnalytics;